import React, { useState } from 'react';
import { Login } from 'react-admin';
import { Box, TextField, Button, Typography, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useLogin, useNotify } from 'react-admin';
import { useSearchParams } from 'react-router-dom';

const CustomLoginForm = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [searchParams] = useSearchParams();
    const login = useLogin();
    const notify = useNotify();

    const invitationId = searchParams.get('invitationId');

    const acceptInvitation = async (
        accessToken: string
    ): Promise<{ tenantCode: string; role?: string; tenant?: any } | null> => {
        if (!invitationId) return null;

        try {
            const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
            const response = await fetch(`${baseUrl}/tenant-invitations/${invitationId}/accept`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (response.ok) {
                const accepted = await response.json();
                return {
                    tenantCode: accepted.tenantCode,
                    role: accepted.role,
                    tenant: accepted.tenant,
                };
            } else {
                console.warn('Failed to accept invitation');
                return null;
            }
        } catch (error) {
            console.warn('Error accepting invitation:', error);
            return null;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await login({ email, password }, '/');

            // If there's a pending invitation, accept it after login
            if (invitationId) {
                const accessToken = localStorage.getItem('accessToken');
                if (accessToken) {
                    const accepted = await acceptInvitation(accessToken);
                    if (accepted) {
                        const localUser = JSON.parse(localStorage.getItem('user') || '{}');
                        const localTenants = Array.isArray(localUser.tenants) ? localUser.tenants : [];
                        const hasAcceptedTenant = localTenants.some(
                            (tenantMembership: any) => tenantMembership?.tenantCode === accepted.tenantCode
                        );

                        if (!hasAcceptedTenant) {
                            localTenants.push({
                                tenantCode: accepted.tenantCode,
                                roles: accepted.role ? [accepted.role] : [],
                                tenant: accepted.tenant,
                            });
                        }
                        localStorage.setItem('user', JSON.stringify({ ...localUser, tenants: localTenants }));
                        if (accepted.tenant) {
                            localStorage.setItem('currentTenant', JSON.stringify(accepted.tenant));
                        }
                        localStorage.removeItem('isStandalone');

                        // Refresh user data so the new tenant is available
                        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
                        const user = JSON.parse(localStorage.getItem('user') || '{}');
                        const userResponse = await fetch(`${baseUrl}/users/${user.id}`, {
                            headers: { Authorization: `Bearer ${accessToken}` },
                        });
                        if (userResponse.ok) {
                            const userWithTenants = await userResponse.json();
                            localStorage.setItem('user', JSON.stringify(userWithTenants));
                            localStorage.removeItem('isStandalone');

                            // Set the accepted tenant as current
                            const acceptedTenant = userWithTenants.tenants?.find(
                                (t: any) => t.tenantCode === accepted.tenantCode
                            );
                            if (acceptedTenant?.tenant) {
                                localStorage.setItem('currentTenant', JSON.stringify(acceptedTenant.tenant));
                            }
                        }
                        notify('Invitation accepted!', { type: 'success' });
                        window.location.href = '/';
                    }
                }
            }
        } catch (error: any) {
            notify(error?.message || 'Invalid email or password', { type: 'warning' });
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
                maxWidth: 400,
            }}
        >
            <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
            />
            <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                aria-label="toggle password visibility"
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ mt: 2 }}
            >
                Sign In
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    <a
                        href="/forgot-password"
                        style={{
                            fontWeight: 'bold',
                            textDecoration: 'none',
                            color: '#1976d2'
                        }}
                    >
                        Forgot your password?
                    </a>
                </Typography>
            </Box>
        </Box>
    );
};

export const LoginPage = () => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minHeight: '100vh',
            backgroundColor: '#3b4d66',
            paddingTop: '15vh',
        }}
    >
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '48px 40px',
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                width: '100%',
                maxWidth: 400,
            }}
        >
            <img
                src="/logo-dv-full-text.png"
                alt="DV Logo"
                style={{
                    height: '40px',
                    marginBottom: '40px',
                }}
            />
            <CustomLoginForm />
        </Box>
    </Box>
);